import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { FunctionComponent } from 'react';
import { useDispatch } from 'react-redux';
import {
  createTaskSimple,
  updateTaskSimple,
  clearCreatedTaskId,
} from '../../../../../../../app/page/page-storage/tasks-simple/tasks-simple.actions';
import { taskSimpleSelectors } from '../../../../../../../app/resource/task-simple/task-simple.selectors';
import { tasksSimplePageSelectors } from '../../../../../../../app/page/page-storage/tasks-simple/tasks-simple.selectors';
import { ITaskSimple, ITaskSimpleCreate } from '../../../../../../../app/resource/task-simple/task-simple.types';
import { useSelect, useSelectWithParams } from '../../../../../../../utils/selector.utils';
import { IHeaderProps } from './types';
import { authSelectors } from '../../../../../../../app/auth/auth.selectors';
import { boardListSelectors } from '../../../../../../../app/resource/boards/boardList/boardList.selectors';
import { useApiRequestState } from '../../../../../../../utils/api.utils';
import { FaLock, FaPaperPlane, FaUnlock } from 'react-icons/fa';

export const Header: FunctionComponent<IHeaderProps> = ({ taskId, boardListId, onClose, setNewTaskId, meetingId }) => {
  const loggedUserId = useSelect(authSelectors.selectLoggedUser);
  const boardList = useSelectWithParams(boardListSelectors.selectResourceById, boardListId ?? '');
  const task = useSelectWithParams(taskSimpleSelectors.selectResourceById, taskId ?? '') as ITaskSimple;
  const lastCreatedTaskId = useSelect(tasksSimplePageSelectors.selectLastCreatedTaskId);
  const dispatch = useDispatch();
  const [title, setTitle] = useState(task ? task.title : '');
  const [isLocked, setIsLock] = useState(task && task.isLocked);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (task) {
      setTitle(task.title);
      setIsLock(task.isLocked);
    }
  }, [task]);

  useEffect(() => {
    if (lastCreatedTaskId) {
      setNewTaskId(lastCreatedTaskId);
      dispatch(clearCreatedTaskId());
    }
  }, [lastCreatedTaskId, setNewTaskId, dispatch]);

  const handleUpdateTaskKey = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === 'Tab') {
      updateTask();
    }
  };

  const handleUpdateTaskButton = () => {
    updateTask();
  };

  const handleLockTaskButtonClick = () => {
    dispatch(
      updateTaskSimple(task.id, {
        isLocked: !isLocked,
      }),
    );
  };

  const handleTitleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const relatedTarget = event.relatedTarget as Node | null;
    if (buttonRef.current && buttonRef.current.contains(relatedTarget)) {
      return; // If event is started by the save (paperplane) button, do nothing
    }

    updateTask();
  };

  const updateTask = useCallback(async () => {
    if (task && title) {
      dispatch(
        updateTaskSimple(task.id, {
          title: title,
        }),
      );
    } else {
      if (title) {
        const newData: ITaskSimpleCreate = {
          title: title,
          description: '',
          userId: loggedUserId ? loggedUserId : '',
          meetingIds: [meetingId],
          status: '0',
          labels: [],
          members: boardListId ? [] : loggedUserId ? [loggedUserId] : [],
          showInTasks: boardListId ? '0' : '1',
          taskTypeId: boardListId,
          boardId: boardList?.boardId ?? undefined,
          isLocked: false,
        };

        dispatch(createTaskSimple(newData));
      }
    }
  }, [dispatch, task, title, meetingId, boardListId, boardList, loggedUserId]);

  const onChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const updatedTitle = event.target.value;
    setTitle(updatedTitle);
  }, []);

  const [showCreateLoader] = useApiRequestState('createTaskApi');
  const [showUpdateLoader] = useApiRequestState('updateTaskApi');

  return (
    <div
      className={`panel-header small-header d-flex align-items-center ${task ? '' : 'focus-title'}`}
      style={{ zIndex: 'auto' }}
    >
      <div style={{ width: '75%' }}>
        <div className="input-wrapper-btn">
          <input
            style={{ height: '35px' }}
            value={title}
            placeholder="Add a title..."
            onChange={onChange}
            onKeyDown={handleUpdateTaskKey}
            onBlur={handleTitleBlur}
          />
          {title && (
            <button ref={buttonRef} onClick={handleUpdateTaskButton}>
              <FaPaperPlane size={15} />
            </button>
          )}
        </div>
      </div>
      {task && task.status === '0' && (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <button className="lock-button" onClick={handleLockTaskButtonClick}>
            {isLocked ? <FaLock className="darkGray" size={15} /> : <FaUnlock className="blueGray" size={15} />}
          </button>
        </div>
      )}
      <div className="d-flex align-items-center ml-auto">
        {showCreateLoader || showUpdateLoader ? (
          <span className="css-loader" style={{ height: 30, width: 30 }}></span>
        ) : (
          <span className="close-popup close" onClick={onClose}>
            <i className="icon-close" />
          </span>
        )}
      </div>
    </div>
  );
};
